import { FC, useEffect, useRef } from 'react'
import { IconPaths, PrescricaoType } from '_metronic/helpers'
import { useAuth } from 'app/modules/auth'
import { ItemPrescricao, podeEditarCadastro, rotuloCatalogo } from '../../core/montagem/itens'
import { useMontagemPrescricao } from '../../hooks/useMontagemPrescricao'
import styles from '../../Prescricao.module.scss'
import { Icone } from '../comum/Icone'
import { useRotulos } from '../comum/useRotulos'
import { GrupoDocumento } from './GrupoDocumento'
import { ID_BOTAO_REVISAR } from './ids'
import { ItemCard } from './ItemCard'
import { Button } from 'app/components'

const ID_TITULO = 'prescricao-atual-titulo'

/** Foco depois que o React aplicar a mudança (o alvo pode ainda não estar na tela). */
const focarDepois = (id: string) => setTimeout(() => document.getElementById(id)?.focus(), 50)

type IProps = {
  montagem: ReturnType<typeof useMontagemPrescricao>
  atendeSUS: boolean
  mostrarErros: boolean
  onRevisar: () => void
  onEditarCadastro: (item: ItemPrescricao) => void
}

/** Prescrição em montagem: itens agrupados pelo documento que vão gerar e a barra de revisão. */
export const PrescricaoAtual: FC<IProps> = ({
  montagem,
  atendeSUS,
  mostrarErros,
  onRevisar,
  onEditarCadastro,
}) => {
  const { t } = useRotulos()
  const { currentUser } = useAuth()
  const { itens, documentos, numeros, pendencias, removido, esquecerRemovido } = montagem
  const total = pendencias.length

  // O item removido some da tela; o foco vai para o "Desfazer" em vez de cair no `body`. O aviso
  // não some sozinho (WCAG 2.2.1): fica até ser fechado ou até a próxima remoção.
  const desfazerRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (removido) desfazerRef.current?.focus()
  }, [removido])

  const desfazer = () => {
    const chave = removido?.chave
    montagem.desfazerRemocao()
    if (chave) focarDepois(`${chave}-cabecalho`)
  }

  const fecharAviso = () => {
    esquecerRemovido()
    focarDepois(ID_TITULO)
  }

  return (
    <section className='card card-bordered mb-5' aria-labelledby={ID_TITULO}>
      <div className='card-header align-items-center border-0 pt-5'>
        <h2 id={ID_TITULO} tabIndex={-1} className='card-title flex-column align-items-start m-0'>
          <span className='fs-4 fw-bold'>{t('prescricao.atual.titulo')}</span>
          <span className={`fs-7 fw-normal ${styles.textoSecundario}`}>
            {t('prescricao.atual.resumo', {
              itens: itens.length,
              documentos: documentos.length,
            })}
          </span>
        </h2>
      </div>

      <div className='card-body pt-2'>
        <div aria-live='polite'>
          {removido && (
            <div className={`${styles.avisoAcao} mb-4`} role='status'>
              <Icone icone={IconPaths.Trash} className={`${styles.textoSecundario} fs-4`} />
              <span className='flex-grow-1'>
                {t('prescricao.atual.removido', {
                  nome:
                    removido.categoria === PrescricaoType.Orientacoes
                      ? removido.catalogo.nome
                      : rotuloCatalogo(removido.catalogo),
                })}
              </span>
              <Button
                ref={desfazerRef}
                size='btn-sm'
                variant='secondary-outline'
                icon={IconPaths.ArrowLeft}
                onClick={desfazer}
              >
                {t('prescricao.atual.desfazer')}
              </Button>
              <Button
                unstyled
                className={styles.fecharAviso}
                onClick={fecharAviso}
                ariaLabel={t('common.close')}
                title={t('common.close')}
              >
                <Icone icone={IconPaths.Close} className='fs-4' />
              </Button>
            </div>
          )}
        </div>

        {itens.length === 0 ? (
          <div className='text-center py-10'>
            <p className='fs-5 fw-bold mb-1'>{t('prescricao.atual.vazio_titulo')}</p>
            <p className={`m-0 ${styles.textoSecundario}`}>{t('prescricao.atual.vazio_texto')}</p>
          </div>
        ) : (
          documentos.map((documento) => (
            <GrupoDocumento key={documento.chave} documento={documento}>
              {documento.itens.map((item) => (
                <ItemCard
                  key={item.chave}
                  item={item}
                  numero={numeros.get(item.chave)}
                  aberto={montagem.aberto === item.chave}
                  atendeSUS={atendeSUS}
                  pendentes={pendencias
                    .filter((pendencia) => pendencia.chave === item.chave)
                    .map((pendencia) => pendencia.campo)}
                  mostrarErros={mostrarErros}
                  podeEditarCadastro={podeEditarCadastro(item.catalogo, currentUser?.pessoa?.id)}
                  onAlternar={() => montagem.alternar(item.chave)}
                  onRemover={() => montagem.remover(item.chave)}
                  onChange={(campos) => montagem.atualizar(item.chave, campos)}
                  onEditarCadastro={() => onEditarCadastro(item)}
                />
              ))}
            </GrupoDocumento>
          ))
        )}
      </div>

      {itens.length > 0 && (
        <div
          className={`${styles.barraAcoes} card-footer d-flex flex-wrap align-items-center justify-content-end gap-4 py-4`}
        >
          <span
            className={`d-inline-flex align-items-center gap-2 fw-bold ${
              total === 0
                ? styles.estadoOk
                : mostrarErros
                ? styles.pendenciaErro
                : styles.pendenciaAviso
            }`}
          >
            <Icone icone={total === 0 ? IconPaths.CircleCheck : IconPaths.Alert} className='fs-3' />
            {total === 0
              ? t('prescricao.atual.sem_pendencias')
              : t('prescricao.atual.pendencias', { quantidade: total })}
          </span>
          <Button id={ID_BOTAO_REVISAR} className='btn-primary' onClick={onRevisar}>
            {t('prescricao.atual.revisar')}
          </Button>
        </div>
      )}
    </section>
  )
}
